import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Bell, Trophy, CreditCard, Settings,
  ChevronLeft, ChevronRight, Zap, Shield, Star, Layers, Briefcase,
  Handshake, FolderKanban
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { useSTNAuth } from '@/context/STNAuthContext';
import { hasFeature } from '@/lib/featureAccess';
import { cn } from '@/lib/utils';
import { useBranding } from '@/lib/branding';

// `feature` = which featureAccess key must be granted for this item to show.
// Items without a `feature` are always shown (core navigation).
const navItems = [
  { path: '/dashboard',           icon: LayoutDashboard, label: 'Dashboard',        feature: 'dashboard' },
  { path: '/interns',             icon: Briefcase,       label: 'Interns',          feature: 'interns_hiring' },
  { path: '/partners',            icon: Handshake,       label: 'Partners',         feature: 'partners' },
  { path: '/mentors',             icon: Star,            label: 'Mentors',          feature: 'mentors' },
  { path: '/mentors?tab=batches', icon: Layers,          label: 'Batches',          feature: 'batches_self' },
  { path: '/partner-batches',     icon: FolderKanban,    label: 'Partner Batches',  feature: 'batches_assigned' },
  { path: '/notifications',       icon: Bell,            label: 'Notifications', badge: true, feature: 'notifications' },
  { path: '/gamification',        icon: Trophy,          label: 'Gamification',     feature: 'gamification' },
  { path: '/billing',             icon: CreditCard,      label: 'Billing',          feature: 'billing' },
  { path: '/settings',            icon: Settings,        label: 'Settings',         feature: 'settings' },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const { branding } = useBranding();
  const location = useLocation();
  const { unreadCount, currentPlan } = useApp();
  const { founder, startup } = useSTNAuth();
  const visibleItems = navItems.filter((item) => hasFeature(startup, item.feature));

  // Real logged-in founder + startup (was hardcoded "Vikram Agarwal / TechVision AI").
  const displayName = founder?.name || 'Founder';
  const companyName = startup?.companyName || '';
  const initials = displayName.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'F';

  const planConfig = {
    free: { label: 'Free Plan', className: 'plan-free' },
    plus: { label: 'Plus Plan', className: 'plan-plus' },
    pro: { label: 'Pro Plan', className: 'plan-pro' },
  };
  const plan = planConfig[startup?.plan || currentPlan] || planConfig.free;

  return (
    <aside
      className={cn(
        'flex flex-col h-screen border-r border-border bg-card transition-all duration-300',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Logo */}
      <div className={cn('flex items-center h-16 px-4 border-b border-border', collapsed ? 'justify-center' : 'gap-3')}>
        <div className="flex-shrink-0 w-8 h-8 rounded-lg gradient-primary flex items-center justify-center overflow-hidden">
          {startup?.logoUrl
            ? <img src={startup.logoUrl} alt="logo" className="w-full h-full object-contain bg-white" />
            : <Zap className="w-4 h-4 text-primary-foreground" />}
        </div>
        {!collapsed && (
          <div>
            {/* The logo slot above shows the FOUNDER'S OWN company logo, which
                is more specific than tenant branding and correctly wins — only
                the product name follows the deployment's brand. */}
            <span className="font-bold text-foreground text-sm tracking-tight">
              {branding.is_platform ? 'StartupNexus' : branding.display_name}
            </span>
            <div className={cn('text-xs px-1.5 py-0.5 rounded-full font-medium mt-0.5 inline-block', plan.className)}>
              {plan.label}
            </div>
          </div>
        )}
      </div>

      {/* User */}
      {!collapsed && (
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
              {startup?.logoUrl
                ? <img src={startup.logoUrl} alt="" className="w-full h-full object-cover" />
                : <span className="text-xs font-bold text-primary-foreground">{initials}</span>}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
              {companyName && <p className="text-xs text-muted-foreground truncate">{companyName}</p>}
            </div>
            {startup?.dpiit && (
              <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {visibleItems.map((item) => {
          const [itemPath, itemQuery] = item.path.split('?');
          const currentSearch = location.search.replace(/^\?/, '');
          let isActive = location.pathname === itemPath;
          if (itemQuery) {
            // Query-scoped item (e.g. /mentors?tab=batches)
            isActive = isActive && currentSearch.includes(itemQuery);
          } else if (itemPath === '/mentors') {
            // Base Mentors item is active only when no tab= query param is present
            isActive = isActive && !currentSearch.includes('tab=');
          }
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'sidebar-item',
                isActive && 'active',
                collapsed && 'justify-center px-2'
              )}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {!collapsed && item.badge && unreadCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
              {collapsed && item.badge && unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2">
        <button
          onClick={onToggle}
          className={cn(
            'sidebar-item w-full',
            collapsed && 'justify-center px-2'
          )}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
};
